/**
 * Carrier logo registry — maps the carrier names Techficient (and Compulife,
 * loosely) hand back to a high-resolution favicon URL via Google's
 * favicon-CDN. Google's `s2/favicons` endpoint is free, no-auth, served from
 * Google's edge and survives carrier-site outages because it's cached for ~24h.
 *
 * Lookup is fuzzy — punctuation is ignored and a key matches whole words
 * anywhere in the name, because Techficient ships variations:
 *   "Banner Life"           vs "Banner Life Insurance Company"
 *   "Foresters"             vs "Foresters Financial"
 *   "SBLI"                  vs "SBLI (Savings Bank Life Insurance)"
 *   "American Amicable"     vs "American-Amicable Life Ins Co of TX"
 *
 * Add entries as we appoint new carriers — first match wins, so a key that a
 * broader one would also match goes first ("pacific guardian" before
 * "guardian"). Check a new domain on the favicon CDN first: for a domain it
 * has no icon for, Google answers with a 16px globe, not an error.
 */

interface CarrierLogoEntry {
  /** Lowercase words, punctuation dropped — first match wins. */
  match: string;
  /** Apex domain for Google's favicon CDN. */
  domain?: string;
  /** Direct icon URL, for a carrier Google has no icon for. */
  logo?: string;
  /** Display brand color for the fallback initial badge. */
  brandColor?: string;
}

// Ordered most-specific → least-specific (longer/distinctive names first).
const REGISTRY: CarrierLogoEntry[] = [
  // Names a broader key below would otherwise claim (first match wins)
  { match: "first penn pacific", domain: "lincolnfinancial.com" }, // Lincoln, not Pacific Life
  { match: "national guardian", domain: "nglic.com" }, // not Guardian
  { match: "pacific guardian", domain: "pacificguardian.com" }, // not Guardian
  { match: "national catholic society of foresters", domain: "ncsf.com" }, // not Foresters Financial
  { match: "sbli usa", domain: "prosperitylife.com" }, // Prosperity Life's, not SBLI of MA
  { match: "corebridge", domain: "corebridgefinancial.com" }, // "Corebridge Financial (formerly AIG Life)", not AIG
  { match: "trans continental" }, // not "continental life" (Aetna); no icon

  // Techficient catalog (verified via /v1/techficient/products on staging)
  { match: "guaranteed heritage", domain: "ghlic.com", brandColor: "#1e40af" },
  { match: "american amicable", domain: "americanamicable.com", brandColor: "#0d4d92" },
  { match: "american home life", brandColor: "#1f4e79" }, // ahlic.com is parked; the carrier publishes no icon
  { match: "liberty bankers", domain: "lbig.com", brandColor: "#1e3a8a" }, // 16px icon only
  { match: "mutual of omaha", domain: "mutualofomaha.com", brandColor: "#003c71" },
  { match: "royal neighbors", domain: "royalneighbors.org", brandColor: "#003366" },
  { match: "centrian", domain: "centrianlife.com", brandColor: "#003769" },
  { match: "transamerica", domain: "transamerica.com", brandColor: "#0033a0" },
  { match: "banner life", domain: "bannerlife.com", brandColor: "#005eb8" },
  { match: "foresters", domain: "foresters.com", brandColor: "#603048" }, // 16px icon only
  { match: "united home life", domain: "unitedhomelife.com", brandColor: "#002855" },
  { match: "uhl", domain: "unitedhomelife.com", brandColor: "#002855" },
  { match: "aflac", domain: "aflac.com", brandColor: "#000000" },
  { match: "sbli", domain: "sbli.com", brandColor: "#0033a0" },

  // Common Compulife / general-market carriers (kept here so the same logo
  // helper works in non-MRFG quote tables too)
  { match: "lincoln financial", domain: "lincolnfinancial.com", brandColor: "#003c71" },
  { match: "john hancock", domain: "johnhancock.com", brandColor: "#1e3a8a" },
  { match: "prudential", domain: "prudential.com", brandColor: "#003c71" },
  { match: "metlife", domain: "metlife.com", brandColor: "#0061a0" },
  { match: "new york life", domain: "newyorklife.com", brandColor: "#003c71" },
  { match: "northwestern mutual", domain: "www.northwesternmutual.com", brandColor: "#0033a0" },
  { match: "guardian", domain: "guardianlife.com", brandColor: "#0033a0" },
  { match: "principal", domain: "principal.com", brandColor: "#0033a0" },
  { match: "pacific life", domain: "pacificlife.com", brandColor: "#1e3a8a" },
  { match: "protective", domain: "protective.com", brandColor: "#003c71" },
  { match: "nationwide", domain: "nationwide.com", brandColor: "#0033a0" }, // 16px icon only
  { match: "ameritas", domain: "ameritas.com", brandColor: "#003c71" },
  { match: "ethos", domain: "ethoslife.com", brandColor: "#000000" },
  { match: "fabric", domain: "meetfabric.com", brandColor: "#000000" },
  { match: "haven life", domain: "havenlife.com", brandColor: "#000000" },
  { match: "bestow", domain: "bestow.com", brandColor: "#000000" },
  { match: "ladder", domain: "ladderlife.com", brandColor: "#000000" },

  // Every other carrier in prod policies + the carriers table (10-31). Each
  // source was checked: Google returns a real icon for the domain (not its
  // 16px globe) and the site is that carrier. Old names and subsidiaries use
  // the current parent's mark (Monumental → Transamerica, SunAmerica →
  // Corebridge…). `logo` = no Google icon, the carrier's own site has one.
  { match: "aaa life", domain: "aaalife.com" },
  { match: "accendo", domain: "aetna.com" },
  { match: "aetna", domain: "aetna.com" },
  { match: "aig", domain: "aig.com" },
  { match: "allianz", domain: "www.allianzlife.com" },
  { match: "allstate", domain: "www.allstate.com" },
  { match: "amalgamated life", domain: "amalgamatedlife.com" },
  { match: "american equity", domain: "american-equity.com" },
  { match: "american fidelity", domain: "americanfidelity.com" },
  { match: "american general", domain: "corebridgefinancial.com" },
  { match: "american income life", domain: "ailife.com" },
  { match: "american international group", domain: "aig.com" },
  { match: "american national", domain: "americannational.com" },
  { match: "american united life", domain: "oneamerica.com" },
  { match: "amica", domain: "amica.com" },
  { match: "anthem", domain: "anthem.com" },
  { match: "aspida", domain: "aspida.com" },
  { match: "assurity", domain: "assurity.com" },
  { match: "athene", domain: "athene.com" },
  { match: "augustar", domain: "augustarfinancial.com" },
  { match: "axa equitable", domain: "equitable.com" },
  { match: "baltimore life", domain: "baltlife.com" },
  { match: "bankers fidelity", domain: "bankersfidelity.com" }, // 28px
  { match: "boston mutual", domain: "bostonmutual.com" },
  { match: "brighthouse", domain: "brighthousefinancial.com" },
  { match: "camico", domain: "camico.com" },
  { match: "catholic financial", logo: "https://www.catholicfinanciallife.org/CFL/Common/favicon/safari-pinned-tab.svg" },
  { match: "central states", domain: "cso.com" },
  { match: "christian fidelity", domain: "christianfidelity.com" },
  { match: "cica", domain: "citizensinc.com" },
  { match: "cigna", domain: "cigna.com" },
  { match: "colonial life", domain: "coloniallife.com" },
  { match: "colonial penn", domain: "colonialpenn.com" },
  { match: "columbian", domain: "cfglife.com" },
  { match: "columbus life", domain: "columbuslife.com" },
  { match: "companion life", domain: "companionlife.com" },
  { match: "continental life", domain: "aetna.com" },
  { match: "cuna mutual", domain: "trustage.com" },
  { match: "cvs health", domain: "cvshealth.com" },
  { match: "dearborn", domain: "dearbornlife.com" },
  { match: "delaware life", domain: "delawarelife.com" }, // 28px
  { match: "encova", domain: "encova.com" },
  { match: "equitable financial", domain: "equitable.com" },
  { match: "f&g", domain: "fglife.com" },
  { match: "family heritage", domain: "familyheritagelife.com" },
  { match: "farmers life", domain: "farmers.com" },
  { match: "farmers new world", domain: "farmers.com" },
  { match: "federal life", domain: "federallife.com" },
  { match: "federated life", domain: "federatedinsurance.com" },
  { match: "fidelity & guaranty", domain: "fglife.com" },
  { match: "fidelity life association", domain: "fidelitylife.com" },
  { match: "first colony", domain: "genworth.com" },
  { match: "forethought", domain: "forethought.com" },
  { match: "funeral directors life", domain: "funeraldirectorslife.com" },
  { match: "garden state life", domain: "americannational.com" },
  { match: "gbu", domain: "gbu.org" },
  { match: "gerber life", domain: "gerberlife.com" },
  { match: "gleaner", domain: "gleanerlife.org" },
  { match: "global atlantic", domain: "globalatlantic.com" },
  { match: "globe life", domain: "globelifeinsurance.com" },
  { match: "golden rule", domain: "uhc.com" },
  { match: "gore mutual", domain: "goremutual.ca" },
  { match: "grange", domain: "grangeinsurance.com" },
  { match: "guarantee trust", domain: "gtlic.com" },
  { match: "guaranty income", domain: "gilico.com" },
  { match: "hartford", domain: "thehartford.com" },
  { match: "healthmarkets", domain: "healthmarkets.com" },
  { match: "humana", domain: "humana.com" },
  { match: "instabrain", domain: "fidelitylife.com" },
  { match: "investors heritage", domain: "investorsheritage.com" },
  { match: "jackson national", domain: "jackson.com" },
  { match: "kansas city life", domain: "kclife.com" },
  { match: "lafayette life", domain: "lafayettelife.com" },
  { match: "legal & general", domain: "lgamerica.com" },
  { match: "liberty mutual", domain: "libertymutual.com" },
  { match: "lifeshield", domain: "lifeshieldnational.com" },
  { match: "lincoln heritage", domain: "lhlic.com" },
  { match: "lincoln national", domain: "lincolnfinancial.com" },
  { match: "locomotive engineers", domain: "lecmpa.org" },
  { match: "loyal american", domain: "cigna.com" }, // loyalamerican.com no longer resolves
  { match: "lumico", domain: "lumico.com" },
  { match: "manhattan life", logo: "https://www.manhattanlife.com/android-icon-192x192.png" },
  { match: "manhattanlife", logo: "https://www.manhattanlife.com/android-icon-192x192.png" },
  { match: "massachusetts mutual", domain: "massmutual.com" },
  { match: "massmutual", domain: "massmutual.com" },
  { match: "members life", domain: "trustage.com" },
  { match: "midland national", domain: "www.midlandnational.com" },
  { match: "minnesota life", domain: "securian.com" },
  { match: "minnesota mutual", domain: "securian.com" },
  { match: "monumental life", domain: "transamerica.com" },
  { match: "mony", domain: "protective.com" },
  { match: "mountain life", domain: "mountainlife.com" },
  { match: "mutual of america", domain: "mutualofamerica.com" },
  { match: "mutual trust life", domain: "palig.com" },
  { match: "nassau", domain: "nfg.com" },
  { match: "national benefit life", logo: "https://www.nationalbenefitlife.com/apple-touch-icon.png" },
  { match: "national income life", domain: "ailife.com" },
  { match: "new era life", domain: "neweralife.com" },
  { match: "north american company", domain: "northamericancompany.com" },
  { match: "nylife", domain: "newyorklife.com" },
  { match: "occidental life", domain: "occidentallife.com" },
  { match: "oceanview", domain: "oceanviewlife.com" },
  { match: "ohio national", domain: "ohionational.com" },
  { match: "old american", domain: "kclife.com" },
  { match: "old republic", domain: "oldrepublic.com" },
  { match: "omaha supplemental", domain: "mutualofomaha.com" },
  { match: "oneamerica", domain: "oneamerica.com" },
  { match: "oxford life", domain: "oxfordlife.com" },
  { match: "pan american life", domain: "palig.com" },
  { match: "pekin", domain: "pekininsurance.com" },
  { match: "penn insurance and annuity", domain: "pennmutual.com" },
  { match: "penn mutual", domain: "pennmutual.com" },
  { match: "pharmacists mutual", domain: "phmic.com" },
  { match: "philadelphia american", domain: "neweralife.com" },
  { match: "phoenix life", domain: "nfg.com" },
  { match: "physicians life", domain: "physicianslife.com" },
  { match: "physicians mutual", domain: "physiciansmutual.com" },
  { match: "polish roman catholic", domain: "prcua.org" },
  { match: "prosperity life", domain: "prosperitylife.com" },
  { match: "pruco", domain: "prudential.com" },
  { match: "pyramid life", domain: "pyramidlife.com" },
  { match: "rli", domain: "rlicorp.com" },
  { match: "securian", domain: "securian.com" },
  { match: "security mutual", domain: "smlny.com" },
  { match: "security plan", domain: "citizensinc.com" },
  { match: "senior life", domain: "seniorlifeinsurancecompany.com" },
  { match: "sentinel security", domain: "sslco.com" },
  { match: "sentry", domain: "sentry.com" },
  { match: "settlers life", domain: "settlerslife.com" },
  { match: "shelter", domain: "shelterinsurance.com" },
  { match: "shenandoah", domain: "prosperitylife.com" },
  { match: "silac", domain: "silacins.com" },
  { match: "sons of norway", domain: "sonsofnorway.com" },
  { match: "standard insurance", domain: "standard.com" },
  { match: "standard life and accident", domain: "americannational.com" },
  { match: "state farm", domain: "statefarm.com" },
  { match: "sun life", domain: "sunlife.com" },
  { match: "sunamerica", domain: "corebridgefinancial.com" },
  { match: "sunset life", domain: "kclife.com" },
  { match: "swiss re", domain: "swissre.com" },
  { match: "symetra", domain: "symetra.com" },
  { match: "teachers insurance and annuity", domain: "www.tiaa.org" },
  { match: "texas life", domain: "www.texaslife.com" },
  { match: "thrivent", domain: "thrivent.com" },
  { match: "tier one", domain: "tieroneinsurance.com" },
  { match: "trinity life", domain: "trinitylifeinsurance.com" },
  { match: "trustage", domain: "trustage.com" },
  { match: "trustmark", domain: "trustmarkbenefits.com" },
  { match: "union central", domain: "ameritas.com" },
  { match: "union labor life", domain: "ullico.com" },
  { match: "united american", domain: "unitedamerican.com" },
  { match: "united farm family", logo: "https://www.infarmbureau.com/apple-touch-icon.png" },
  { match: "united heritage", logo: "https://www.unitedheritage.com/images/icons/uhfg_icon_256.png" },
  { match: "united investors", domain: "protective.com" },
  { match: "united of omaha", domain: "mutualofomaha.com" },
  { match: "united states life", domain: "corebridgefinancial.com" },
  { match: "unitedhealthcare", domain: "uhc.com" },
  { match: "universal fidelity", domain: "uflic.com" },
  { match: "usaa", domain: "usaa.com" },
  { match: "usable", domain: "usablelife.com" },
  { match: "voya", domain: "voya.com" },
  { match: "washington national", domain: "washingtonnational.com" },
  { match: "west coast life", domain: "protective.com" },
  { match: "western & southern", domain: "westernsouthern.com" },
  { match: "western reserve life", domain: "transamerica.com" },
  { match: "william penn association", domain: "wpalife.org" },
  { match: "william penn life", domain: "lgamerica.com" },
  { match: "wilton", domain: "wiltonre.com" },
  { match: "woman s life", domain: "womanslife.org" },
  { match: "woodmen of the world", domain: "woodmenlife.org" },
  { match: "zurich", domain: "zurichna.com" },

  // Only 16px icons exist for these, on their own sites too: CarrierLogo shows
  // initials on brandColor (sampled from that icon) instead of a blur. The
  // domain stays so a bigger icon shows up by itself.
  { match: "americo", domain: "americo.com", brandColor: "#003060" },
  { match: "great southern life", domain: "americo.com", brandColor: "#003060" },
  { match: "unum", domain: "unum.com", brandColor: "#004890" },
  { match: "paul revere", domain: "unum.com", brandColor: "#004890" },
  { match: "provident life", domain: "unum.com", brandColor: "#004890" },
  { match: "starmount", domain: "unum.com", brandColor: "#004890" },
  { match: "primerica", domain: "primerica.com", brandColor: "#181818" },
  { match: "national life group", domain: "nationallife.com", brandColor: "#41822b" },
  { match: "modern woodmen", domain: "modernwoodmen.org" },
  { match: "national western", domain: "nationalwesternlife.com", brandColor: "#004890" },
  { match: "cincinnati life", domain: "cinfin.com", brandColor: "#004878" },
  { match: "security benefit", domain: "securitybenefit.com", brandColor: "#487890" },
  { match: "reliance standard", domain: "reliancestandard.com", brandColor: "#8c6923" },
  { match: "foremost", domain: "foremost.com", brandColor: "#0060a8" },
  { match: "sagicor", domain: "sagicor.com", brandColor: "#4f7e1f" },
  { match: "homesteaders", domain: "homesteaderslife.com", brandColor: "#304890" },
  { match: "great west", domain: "empower.com" },
  { match: "illinois mutual", domain: "illinoismutual.com", brandColor: "#006060" },
  { match: "reserve national", domain: "kemper.com", brandColor: "#001848" },
  { match: "capitol life", domain: "capitollife.com", brandColor: "#3048a8" },
  { match: "world insurance", domain: "worldinsco.com", brandColor: "#181818" },
];

export interface CarrierLogoMatch {
  /** Icon URL (Google favicon CDN, sized for the requested pixel density),
   *  or null for a known carrier with no usable icon — initials on brandColor. */
  url: string | null;
  /** Brand color for the surrounding chip / hover state. */
  brandColor: string;
}

/**
 * Resolve a carrier name to a logo URL. Returns null when no entry matches
 * — caller should fall back to an initial badge.
 *
 * @param name      Free-text carrier name from a quote / case row.
 * @param sizePx    Logo size (Google rounds up to 16/32/64/128). Default 64
 *                  which renders crisply at 32px display sizes on retina.
 */
export function getCarrierLogo(
  name: string | undefined | null,
  sizePx: 16 | 32 | 64 | 128 = 64,
): CarrierLogoMatch | null {
  if (!name) return null;
  const words = String(name).toLowerCase().replace(/[^a-z0-9&]+/g, " ").trim();
  if (!words) return null;
  const key = ` ${words} `;
  for (const entry of REGISTRY) {
    if (key.includes(` ${entry.match} `)) {
      return {
        url:
          entry.logo ??
          (entry.domain ? `https://www.google.com/s2/favicons?domain=${entry.domain}&sz=${sizePx}` : null),
        brandColor: entry.brandColor || "#1e293b",
      };
    }
  }
  return null;
}

/**
 * Initials for the fallback badge. Trims "Insurance Company", "Life", etc.
 * so "American Amicable Insurance Company" → "AA" not "AI".
 */
export function carrierInitials(name: string | undefined | null): string {
  if (!name) return "??";
  const cleaned = String(name)
    .replace(
      /\b(insurance|life|company|co\.?|corp\.?|corporation|group|inc\.?|llc|the)\b/gi,
      "",
    )
    .replace(/\s+/g, " ")
    .trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
